import { useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { FMSLogger } from "FMSLogger";
import { CONFIRMATION_TYPES } from "Components/common/constants";
import { getFleetMissions, getReleasedStops } from "Slices/operatorData";
import { getVehiclesDict } from "Slices/vehicles";
import { getVehiclesUpdateData } from "Slices/vehiclesState";
import { getVehicleTypes } from "Slices/vehicleTypes";
import { getStopsByCurrentSemanticMap } from "Slices/stops";
import { getOperatorStations } from "Slices/operatorStation";
import { getCurrentSemanticMap } from "Slices/semanticMaps";
import { getDispatchOriginsByMissionId } from "Slices/dispatchOrigins";
import { InProgressVehicleListItemData } from "../components/InProgressVehicleListItem";
import { resolveLiveBattery } from "../utils/liveBattery";
import {
  PreparedTrack,
  SnapResult,
  arcLengthBetween,
  prepareTrack,
  snapPoint,
} from "../utils/trackDistance";

const trackDistanceLogger = FMSLogger.byPrefix("station-view:track-distance");

const clampPercent = (n: number): number =>
  Math.min(100, Math.max(0, Number.isFinite(n) ? n : 0));

// DEV-3638: split statuses by their cloud-core semantics. Mirrors
// `QueuedMission.ACTIVE_STATUSES` in cloud-core's missions/models.py
// (IN_PROGRESS / PAUSED / BLOCKED_BY_MANUAL = genuinely executing on the
// vehicle). SENT/RECEIVED are *queue* statuses meaning "dispatched/ack'd but
// not yet running" — at most one per vehicle (head of queue) should surface
// in the Station "In Progress" tab as the next-up approaching mission.
const TRULY_ACTIVE_STATUSES = new Set<MissionStatusCode>([
  "IN_PROGRESS",
  "PAUSED",
  "BLOCKED_BY_MANUAL",
]);
const NEXT_UP_STATUSES = new Set<MissionStatusCode>(["SENT", "RECEIVED"]);

const HMI_RELEASE_TYPES = new Set<string>([
  CONFIRMATION_TYPES.CLOUD_HMI_RELEASE_CONFIRMATION,
  CONFIRMATION_TYPES.CLOUD_RELEASE_CONFIRMATION,
]);

/**
 * True when `vehicleId` has a sibling truly-active mission that is parked at
 * a stop requiring MANUAL_RELEASE — i.e. another mission on this vehicle's
 * queue cannot start (or, for loop missions, cannot resume) until an operator
 * physically releases the blocker. DEV-3638: uses TRULY_ACTIVE_STATUSES so a
 * PAUSED or BLOCKED_BY_MANUAL blocker also suppresses sibling rows, matching
 * the eligibility gate that admits all three statuses.
 */
const isVehicleAwaitingManualRelease = (
  vehicleId: string | undefined,
  fleetMissions: QueuedMission[]
): boolean => {
  if (!vehicleId) return false;
  for (const m of fleetMissions) {
    if (m.vehicle !== vehicleId) continue;
    if (!TRULY_ACTIVE_STATUSES.has(m.status)) continue;
    const passed = m.passedStops ?? 0;
    if (passed <= 0) continue;
    const cur = m.stops?.[passed - 1];
    if (!cur) continue;
    if (cur.confirmation_type === CONFIRMATION_TYPES.MANUAL_RELEASE)
      return true;
  }
  return false;
};

const resolveStationLabel = (
  stop: QueuedMissionStop | undefined,
  stops: Stop[] | undefined,
  fallback: string
): string => {
  if (!stop) return fallback;
  const mapped = stop.original_stop_id
    ? stops?.find((s) => s.id === stop.original_stop_id)?.shortName
    : undefined;
  return mapped || stop.name || fallback;
};

/**
 * DEV-3638: per-vehicle eligibility map for the Station "In Progress" panel.
 * The fleet-mission stream from /fleets/mission_queue/ includes every
 * NEW/SENT/RECEIVED/IN_PROGRESS mission for the fleet, so a vehicle can have
 * multiple queued missions targeting the picked station while it's busy
 * executing an earlier one. We admit:
 *   - every mission whose status is in TRULY_ACTIVE_STATUSES, and
 *   - for each vehicle that has NO truly-active mission, the single NEXT_UP
 *     mission with the lowest `order` (head of the queue).
 * Everything else is queued behind the active mission and must not surface
 * as "In Progress" yet. Pure helper — extracted from the hook for direct
 * unit testability.
 *
 * The head pick is by `order` ascending. Cloud-core orders the queue by
 * `(-priority, order, -time_stamp)` (see QueuedMission.Meta.ordering), but
 * `set_hot_job` always shifts the hot mission to `order=0`, so `order` alone
 * suffices in practice. cloud-core's `OrderedModel` keeps `order` a unique,
 * mandatory integer within each ordering scope, so ties don't occur in real
 * data — but the `fleetMissions` stream order is not guaranteed, so we break
 * any tie (or absent `order`) deterministically by mission `id`. That keeps
 * the chosen head stable regardless of arrival order.
 */
export const computeEligibleMissions = (
  fleetMissions: QueuedMission[]
): Map<string, Set<string>> => {
  const eligibleByVehicle = new Map<string, Set<string>>();
  const queuedByVehicle = new Map<string, QueuedMission[]>();
  for (const m of fleetMissions) {
    if (!m.vehicle) continue;
    if (TRULY_ACTIVE_STATUSES.has(m.status)) {
      const s = eligibleByVehicle.get(m.vehicle) ?? new Set<string>();
      s.add(m.id);
      eligibleByVehicle.set(m.vehicle, s);
    } else if (NEXT_UP_STATUSES.has(m.status)) {
      const arr = queuedByVehicle.get(m.vehicle) ?? [];
      arr.push(m);
      queuedByVehicle.set(m.vehicle, arr);
    }
  }
  for (const [vehicleId, queued] of queuedByVehicle) {
    // Vehicle already has a truly-active mission — its queue tail stays hidden.
    if (eligibleByVehicle.has(vehicleId)) continue;
    // `queued` is always non-empty here by construction.
    const headOfQueue = queued.reduce((best, cur) => {
      const curOrder = cur.order ?? Infinity;
      const bestOrder = best.order ?? Infinity;
      if (curOrder !== bestOrder) return curOrder < bestOrder ? cur : best;
      // Deterministic tie-break: lowest id wins, independent of stream order.
      return cur.id < best.id ? cur : best;
    });
    const s = eligibleByVehicle.get(vehicleId) ?? new Set<string>();
    s.add(headOfQueue.id);
    eligibleByVehicle.set(vehicleId, s);
  }
  return eligibleByVehicle;
};

const stopCountProgress = (
  mode: "approaching" | "continuing",
  passedStops: number,
  startIdx: number,
  endIdx: number
): number => {
  if (mode === "approaching") {
    return endIdx > 0 ? clampPercent((passedStops / endIdx) * 100) : 0;
  }
  const span = endIdx - startIdx;
  return span > 0 ? clampPercent(((passedStops - startIdx) / span) * 100) : 100;
};

interface DistanceContext {
  prepared: PreparedTrack;
  /** Map of mission.id → array of snaps (length === mission.stops.length). null entries == unsnappable stop. */
  missionStopSnaps: Map<string, Array<SnapResult | null>>;
}

/**
 * Pre-snap a mission's stops to the prepared track. Returns one entry per stop;
 * any unsnappable stop is null and triggers fallback for whatever leg uses it.
 */
const snapMissionStops = (
  prepared: PreparedTrack,
  mission: QueuedMission
): Array<SnapResult | null> =>
  mission.stops.map((s) => snapPoint(prepared, s.latitude, s.longitude));

/**
 * Sum arc length leg-by-leg over `stops[from..to]` using pre-computed snaps.
 * Returns null if any leg fails to snap cleanly (a stop snap is null OR the
 * leg's snaps land on different lines, indicating a disjoint/garbage track).
 */
const sumLegs = (
  prepared: PreparedTrack,
  snaps: Array<SnapResult | null>,
  fromIdx: number,
  toIdx: number
): number | null => {
  if (toIdx <= fromIdx) return 0;
  // Walk forward, summing only legs whose endpoints both snap cleanly to the
  // same polyline. Skip null snaps (stops outside the map bbox — e.g. seed
  // stops at lat 47 when the route is at lat 37) by chaining over them rather
  // than returning null. As long as we sum at least one valid leg, we have a
  // meaningful total.
  let total = 0;
  let lastValidSnap: SnapResult | null = null;
  for (let i = fromIdx; i <= toIdx; i++) {
    const cur = snaps[i];
    if (!cur) continue;
    if (lastValidSnap !== null) {
      const { meters, sameLine } = arcLengthBetween(
        prepared,
        lastValidSnap,
        cur
      );
      if (sameLine) {
        total += meters;
      }
    }
    lastValidSnap = cur;
  }
  // Always return a number — 0 simply means "no measurable legs in this range"
  // (e.g. the prefix only contains off-track stops). Caller can decide whether
  // a 0-total range is meaningful (e.g. computeDistanceProgress treats 0
  // total-distance as a fallback trigger but accepts 0 partial-distance).
  return total;
};

/**
 * Progress of the vehicle along the CURRENT leg as a straight-line (chord)
 * projection of the vehicle onto the segment stops[startIdx] → stops[endIdx],
 * returned as 0–100 (0 at the from-stop, 100 at the to-stop).
 *
 * The In Progress bar always represents a single leg: every continuing/
 * approaching case that reaches here has endIdx === startIdx + 1 (== the next
 * un-passed stop). So leg progress is fully determined by three points — the
 * two stop coordinates and the live vehicle coordinate — and needs neither the
 * rendered track nor a ratchet.
 *
 * Why chord projection rather than snap + arc-length along the track:
 * the rendered semantic-map LineString is independent of the vehicle's actual
 * path. `nearestPointOnLine` snaps the vehicle and the stops onto whichever
 * lobe of a winding/self-crossing line is closest, which produced two failures
 * we measured directly on the sim walker:
 *   1. wrong-lobe snap at a stop hand-off read ~90% at the very start of a leg
 *      (the marker rendered "in the wrong spot"), and
 *   2. arc length along the winding track between two stops overstated the leg
 *      length ~2.9×, compressing the whole 0→100 sweep into the first ~25% of
 *      the bar so the marker never reached the destination before the leg
 *      handed off.
 * Projecting the vehicle onto the straight chord between the two stops removes
 * both: it is monotonic 0→100 for stop-to-stop travel and free of lobe
 * ambiguity, so it can drive the bar directly with no smoothing latch (which
 * also keeps marker latency well under the 3 s propagation budget).
 *
 * Trade-off (flagged for review): on a genuinely curved real-world leg the
 * chord is a straight-line approximation, so a vehicle following a bowed path
 * reads slightly behind/ahead of true arc progress mid-leg. It is still
 * monotonic and exact at both endpoints, and is strictly better than the
 * arc-length-on-the-wrong-track behaviour it replaces.
 */
const computeDistanceProgress = (
  _ctx: DistanceContext,
  mission: QueuedMission,
  vehicle: Vehicle | undefined,
  _mode: "approaching" | "continuing",
  _passedStops: number,
  startIdx: number,
  endIdx: number
): number | null => {
  if (!vehicle) return null;
  if (
    !Number.isFinite(vehicle.latitude) ||
    !Number.isFinite(vehicle.longitude)
  ) {
    return null;
  }
  const fromStop = mission.stops[startIdx];
  const toStop = mission.stops[endIdx];
  if (
    !fromStop ||
    !toStop ||
    !Number.isFinite(fromStop.latitude) ||
    !Number.isFinite(fromStop.longitude) ||
    !Number.isFinite(toStop.latitude) ||
    !Number.isFinite(toStop.longitude)
  ) {
    return null;
  }

  // Equirectangular projection around the leg's mid-latitude: scaling
  // longitude by cos(lat) makes a degree of longitude metrically comparable to
  // a degree of latitude over the (short) leg. We only ever take a ratio of
  // projections onto the same chord, so the absolute scale cancels and there's
  // no need to convert to meters.
  const midLatRad =
    ((fromStop.latitude + toStop.latitude) / 2) * (Math.PI / 180);
  const kx = Math.cos(midLatRad);
  const legX = (toStop.longitude - fromStop.longitude) * kx;
  const legY = toStop.latitude - fromStop.latitude;
  const legLenSq = legX * legX + legY * legY;
  // Degenerate leg (both stops at the same coordinate, e.g. a single-stop
  // dispatch where startIdx === endIdx === 0): no direction to project onto.
  // Return null so the haversine rescue / stop-count fallback owns it,
  // preserving the prior behaviour for those paths.
  if (!(legLenSq > 0)) return null;

  const vehX = (vehicle.longitude - fromStop.longitude) * kx;
  const vehY = vehicle.latitude - fromStop.latitude;
  const t = (vehX * legX + vehY * legY) / legLenSq;
  return clampPercent(t * 100);
};

export const useInProgressVehicles = (): InProgressVehicleListItemData[] => {
  const fleetMissions = useSelector(getFleetMissions);
  const vehiclesDict = useSelector(getVehiclesDict);
  const vehiclesUpdateData = useSelector(getVehiclesUpdateData);
  const vehicleTypes = useSelector(getVehicleTypes);
  const stops = useSelector(getStopsByCurrentSemanticMap);
  const operatorStations = useSelector(getOperatorStations);
  const currentSemanticMap = useSelector(getCurrentSemanticMap);
  const releasedStops = useSelector(getReleasedStops);
  // FE-only tracking of the operator's picked stop at the moment each mission
  // was dispatched. Used to identify dispatch-departing missions correctly
  // without relying on mission.stationStopId, which is derived from the
  // mission's first stop (not the operator's active pick at dispatch time).
  const dispatchOrigins = useSelector(getDispatchOriginsByMissionId);

  const selectedStationId = useMemo(
    () => operatorStations.find((each) => each.isPicked)?.id,
    [operatorStations]
  );

  // Memoize the prepared track per semantic map id. Pinning to id (not the
  // SemanticMap object reference) avoids redoing the work when the slice
  // re-emits the same map post-WebSocket tick.
  const preparedTrack = useMemo<PreparedTrack | null>(() => {
    if (!currentSemanticMap?.id) return null;
    return prepareTrack(currentSemanticMap.track, currentSemanticMap.id);
  }, [currentSemanticMap?.id, currentSemanticMap?.track]);

  // Memoize per-mission stop snaps keyed by `${semanticMapId}:${missionId}`
  // across renders. This survives WebSocket-driven re-renders (vehicle position
  // changes) so we only re-snap stops if the mission or the map changes.
  const missionSnapCacheRef = useRef<Map<string, Array<SnapResult | null>>>(
    new Map()
  );

  // Per-mission progress ratchet, keyed by `${missionId}:${passedStops}:${mode}`.
  // Enforces (1) no backward progress, (2) no progress past the next un-passed
  // stop's bar fraction, and (3) a velocity cap so a noisy telemetry frame
  // can't teleport the slider forward faster than the vehicle's max speed.
  // Reset cleanly when passedStops bumps (key change).
  const progressRatchetRef = useRef<
    Map<string, { progress: number; t: number; initialRemaining?: number }>
  >(new Map());
  // Per-mission last-logged signature for SIMHACK-prog. Suppresses re-logging
  // when nothing meaningful has changed (vehicle position, mode, indices,
  // progress). Cuts log volume from ~20/sec/mission to event-driven only.
  const simhackLastSigRef = useRef<Map<string, string>>(new Map());
  // Same-shape dedup for the "distance unavailable" warning so we don't spam
  // it on every render for missions without track-distance precomputed.
  const trackDistanceUnavailableSigRef = useRef<Map<string, string>>(new Map());
  // Same-shape dedup for rescue-bail and rescue-ok diagnostics. Key is
  // `${mission.id}:${reason}` so each bail point deduplicates independently.
  const rescueBailSigRef = useRef<Map<string, string>>(new Map());

  return useMemo(() => {
    if (!selectedStationId) return [];
    if (!Array.isArray(fleetMissions) || fleetMissions.length === 0) return [];

    // DEV-3638: per-vehicle eligibility gate. See computeEligibleMissions.
    const eligibleByVehicle = computeEligibleMissions(fleetMissions);
    const isEligible = (m: QueuedMission): boolean =>
      !!m.vehicle && !!eligibleByVehicle.get(m.vehicle)?.has(m.id);

    // Build a fresh per-call view of the snap cache, scoped to this semantic map.
    const missionStopSnaps = new Map<string, Array<SnapResult | null>>();
    if (preparedTrack) {
      for (const mission of fleetMissions) {
        if (!isEligible(mission)) continue;
        if (!mission.stops || mission.stops.length === 0) continue;
        const cacheKey = `${preparedTrack.semanticMapId}:${mission.id}:${mission.stops.length}`;
        let snaps = missionSnapCacheRef.current.get(cacheKey);
        if (!snaps) {
          snaps = snapMissionStops(preparedTrack, mission);
          missionSnapCacheRef.current.set(cacheKey, snaps);
        }
        missionStopSnaps.set(mission.id, snaps);
      }
    }
    const distanceCtx: DistanceContext | null = preparedTrack
      ? { prepared: preparedTrack, missionStopSnaps }
      : null;

    const rows: InProgressVehicleListItemData[] = [];

    // DEV-3706 (In Progress parity): resolve a vehicle's live online status from
    // the WS slice. vehiclesState is keyed by no-dash hex while vehiclesDict uses
    // dashed UUIDs, so try both (mirrors useActionRequiredVehicles). Only an
    // explicit OFFLINE signal counts — absence of data is treated as online so we
    // never grey a vehicle just because the WS hasn't reported it yet.
    const isVehicleOffline = (vehicleId: string | undefined): boolean => {
      if (!vehicleId || !vehiclesUpdateData) return false;
      const noDashId = vehicleId.replace(/-/g, "");
      const live =
        vehiclesUpdateData[noDashId] || vehiclesUpdateData[vehicleId];
      return live?.online_status === "OFFLINE";
    };

    for (const mission of fleetMissions) {
      if (!isEligible(mission)) continue;
      if (!mission.stops || mission.stops.length === 0) continue;

      const passedStops = mission.passedStops ?? 0;

      // The most recently completed stop drives two checks below: whether
      // this mission is the MANUAL_RELEASE blocker (so we don't suppress
      // its own row), and whether the vehicle just departed the picked
      // station via an HMI/cloud release (triggers continuing-mode).
      const lastCompletedStop =
        passedStops > 0 ? mission.stops[passedStops - 1] : null;

      // Suppress the row whenever this vehicle is parked at a sibling
      // MANUAL_RELEASE stop and this mission isn't itself the blocker.
      // Without this, queued/loop missions render a slider that can't move
      // until an operator physically releases the vehicle elsewhere; the
      // dispatch-station operator sees "Manual Release Required" in the
      // Action Required tab instead.
      const isThisMissionTheBlocker =
        !!lastCompletedStop &&
        lastCompletedStop.confirmation_type ===
          CONFIRMATION_TYPES.MANUAL_RELEASE;
      if (
        !isThisMissionTheBlocker &&
        isVehicleAwaitingManualRelease(mission.vehicle, fleetMissions)
      ) {
        continue;
      }

      const selectedIdxs: number[] = [];
      mission.stops.forEach((s, i) => {
        if (s.original_stop_id === selectedStationId) selectedIdxs.push(i);
      });

      // Continuing-mode trigger: the most recently completed stop is the
      // selected station AND it was released via HMI/cloud confirmation.
      const isJustDeparted =
        !!lastCompletedStop &&
        lastCompletedStop.original_stop_id === selectedStationId &&
        HMI_RELEASE_TYPES.has(lastCompletedStop.confirmation_type);

      // Future visits to selected station (index >= passedStops).
      const futureIdxs = selectedIdxs.filter((i) => i >= passedStops);

      // Auto-departing trigger (DEV-2587 "show the vehicle one station away,
      // to OR from the picked station"): the most recently completed stop IS
      // the selected station and it carried no release gate. HMI/cloud release
      // types are owned by isJustDeparted above (the vehicle is parked at the
      // station until an operator releases it, so it isn't truly departing yet);
      // MANUAL_RELEASE keeps its own awaiting-release suppression. Everything
      // else (CONFIRMATION_TYPE_NONE and other auto pass-throughs) means the
      // vehicle has genuinely cleared the picked station and is one stop away on
      // the next leg, so the "from" segment should render — mirroring how the
      // approaching branch renders the "to" segment regardless of stop type.
      const isAutoDeparted =
        !!lastCompletedStop &&
        lastCompletedStop.original_stop_id === selectedStationId &&
        !HMI_RELEASE_TYPES.has(lastCompletedStop.confirmation_type) &&
        lastCompletedStop.confirmation_type !==
          CONFIRMATION_TYPES.MANUAL_RELEASE;

      let mode: "approaching" | "continuing";
      let startIdx: number;
      let endIdx: number;
      let isDeparting = false;

      if (isJustDeparted) {
        // Only show once the operator has explicitly released the stop.
        const releasedAt = releasedStops[mission.id];
        if (releasedAt === undefined || releasedAt !== passedStops) continue;

        // Per UX rule: render only the IMMEDIATE next segment, not the full
        // remainder of the mission. Without this, a vehicle that just departed
        // the picked station would show a slider spanning to the final stop,
        // even when the actual next stop is something different mid-mission.
        mode = "continuing";
        startIdx = passedStops - 1;
        endIdx = passedStops;
        if (endIdx >= mission.stops.length) continue;
        if (endIdx <= startIdx) continue;
      } else if (isAutoDeparted) {
        // "From" direction for an auto pass-through: the vehicle just cleared the
        // picked station and is on the immediate departing leg. Render only that
        // single segment (picked -> next stop), mirroring the continuing-mode
        // geometry used by isJustDeparted, but without the release gate (auto
        // stops have no parking/confirmation step to wait on). Drop when the
        // picked station was the final stop (no next leg to depart toward).
        mode = "continuing";
        startIdx = passedStops - 1;
        endIdx = passedStops;
        if (endIdx >= mission.stops.length) continue;
        if (endIdx <= startIdx) continue;
      } else if (
        passedStops === 0 &&
        dispatchOrigins[mission.id] === selectedStationId &&
        mission.stops[0]?.original_stop_id !== selectedStationId
      ) {
        // Dispatch-departing — vehicle just dispatched from the picked station
        // (as recorded in the FE-tracked dispatchOrigins map at dispatch time),
        // heading to its first stop, AND that first stop is something other
        // than picked itself. (When the first stop IS picked — e.g., Call
        // Vehicle missions where the destination equals the dispatch origin
        // — fall through to the approaching branch, which has explicit
        // single-stop rendering that suppresses the duplicate-label display.)
        // We use dispatchOrigins[mission.id] instead of mission.stationStopId
        // because stationStopId is derived from the mission's first stop on
        // the BE, not from the operator's active pick at dispatch time.
        // Fires regardless of whether picked appears later in the stops list;
        // the just-dispatched view is always relevant from the dispatching
        // station's perspective until the vehicle physically reaches its
        // first stop.
        mode = "approaching";
        startIdx = 0;
        endIdx = 0;
        isDeparting = true;
      } else if (futureIdxs.length > 0) {
        // Only show as "approaching" when the picked station is the IMMEDIATE
        // next-target stop. Without this, a vehicle that visits the picked station
        // *later* in its mission (after several other stops) appears as
        // "approaching" from this station's view even though it hasn't started
        // that leg — irrelevant noise for the operator.
        if (futureIdxs[0] !== passedStops) continue;
        mode = "approaching";
        // Single-hop "one station away" geometry (DEV-2587): render only the
        // immediate leg the vehicle is on — the stop it most recently left
        // (passedStops - 1) -> the picked station (passedStops) — rather than
        // spanning from the route's first stop. This mirrors the departing
        // (FROM) leg's single-segment geometry, so the window approaching the
        // picked station and the window the vehicle just departed show an
        // identical one-hop line. When passedStops === 0 there is no prior stop
        // (the vehicle is inbound to its first stop from its dispatch origin,
        // which is off the stop list); keep startIdx = 0 so the passedStops ===
        // 0 haversine/rescue path below owns that geometry unchanged.
        startIdx = passedStops === 0 ? 0 : passedStops - 1;
        endIdx = futureIdxs[0];
      } else {
        continue;
      }

      const startStop = mission.stops[startIdx];
      const endStop = mission.stops[endIdx];
      let vehicle = vehiclesDict?.[mission.vehicle];
      // Override stale vehicle.latitude/longitude with live WS data from
      // vehiclesState (Slices/vehicles is only populated at page load and
      // doesn't refresh from WS state events). Try both UUID formats since
      // vehiclesState is keyed by no-dashes hex while vehiclesDict uses dashed.
      if (vehicle) {
        const dashedId = mission.vehicle;
        const noDashId = dashedId ? dashedId.replace(/-/g, "") : "";
        const liveState =
          vehiclesUpdateData?.[noDashId] || vehiclesUpdateData?.[dashedId];
        if (
          liveState &&
          typeof liveState.t_lat === "number" &&
          typeof liveState.t_long === "number"
        ) {
          vehicle = {
            ...vehicle,
            latitude: liveState.t_lat,
            longitude: liveState.t_long,
          };
        }
      }
      const vehicleType = vehicle
        ? vehicleTypes?.find((vt) => vt.id === vehicle.vehicleTypeId)
        : null;

      const fallbackProgress = stopCountProgress(
        mode,
        passedStops,
        startIdx,
        endIdx
      );
      let progress = fallbackProgress;
      let progressSrc: "fallback" | "distance" = "fallback";
      if (distanceCtx) {
        const distProgress = computeDistanceProgress(
          distanceCtx,
          mission,
          vehicle,
          mode,
          passedStops,
          startIdx,
          endIdx
        );
        if (distProgress !== null) {
          progress = distProgress;
          progressSrc = "distance";
        } else {
          const tdSig = `${mode}|${startIdx}-${endIdx}`;
          if (
            trackDistanceUnavailableSigRef.current.get(mission.id) !== tdSig
          ) {
            trackDistanceUnavailableSigRef.current.set(mission.id, tdSig);
            trackDistanceLogger.info(
              "Distance progress unavailable; falling back to stop-count.",
              { missionId: mission.id, mode, startIdx, endIdx }
            );
          }
        }
      }

      let skipDefaultRatchet = false;

      // Dispatch-departing animation: vehicle is heading from a station that's
      // NOT in the mission stops to stops[0]. Snap-based arc-length is
      // unreliable here because distAlongLine on a winding LineString jumps
      // around as the vehicle's nearest-segment changes — the first sample
      // can be a local minimum, which pins the ratchet at 0%. Use straight-
      // line haversine vehicle→stops[0]: monotonic, no segment ambiguity.
      if (
        isDeparting &&
        passedStops === 0 &&
        vehicle &&
        Number.isFinite(vehicle.latitude) &&
        Number.isFinite(vehicle.longitude) &&
        Number.isFinite(mission.stops[0]?.latitude) &&
        Number.isFinite(mission.stops[0]?.longitude)
      ) {
        const R = 6371008.8;
        const toRad = (deg: number) => (deg * Math.PI) / 180;
        const lat1 = toRad(vehicle.latitude);
        const lat2 = toRad(mission.stops[0].latitude);
        const dLat = lat2 - lat1;
        const dLng = toRad(mission.stops[0].longitude - vehicle.longitude);
        const sinDLat = Math.sin(dLat / 2);
        const sinDLng = Math.sin(dLng / 2);
        const h =
          sinDLat * sinDLat +
          Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
        const remaining = 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));

        const rKey = `${mission.id}:${passedStops}:${mode}:remaining`;
        const prev = progressRatchetRef.current.get(rKey);
        // Use max-seen for initialRem so a first sample that happens to land
        // close (e.g. vehicle paused at dispatch origin near stops[0]) doesn't
        // pin the ratchet at 0% if it later moves further before approaching.
        const initialRem = Math.max(prev?.initialRemaining ?? 0, remaining);
        if (initialRem > 0) {
          const raw = clampPercent(
            ((initialRem - remaining) / initialRem) * 100
          );
          const next = Math.max(prev?.progress ?? 0, raw);
          progressRatchetRef.current.set(rKey, {
            progress: next,
            t: Date.now(),
            initialRemaining: initialRem,
          });
          progress = next;
          skipDefaultRatchet = true;
        }
      }

      // Single-stop approaching: computeDistanceProgress returns null because
      // sumLegs(startIdx=0, endIdx=0) = 0 (no arc between the same stop).
      // Measure remaining arc from vehicle to the stop and normalise against
      // the initial reading stored in the ratchet.
      if (
        !skipDefaultRatchet &&
        progressSrc === "fallback" &&
        mode === "approaching" &&
        passedStops === 0 &&
        vehicle &&
        distanceCtx
      ) {
        const missionSnaps = distanceCtx.missionStopSnaps.get(mission.id);
        const stopSnap = missionSnaps?.[0] ?? null;
        if (!stopSnap) {
          const bailKey = `${mission.id}:missionSnap_null`;
          const bailSig = `stops=${mission.stops.length}|s0=${mission.stops[0]?.name}|s0ll=${mission.stops[0]?.latitude},${mission.stops[0]?.longitude}`;
          if (rescueBailSigRef.current.get(bailKey) !== bailSig) {
            rescueBailSigRef.current.set(bailKey, bailSig);
            // eslint-disable-next-line no-console
            console.log(
              `[SIMHACK-rescue-bail] missionName=${mission.name} vehicleName=${vehicle?.name} reason=missionSnap_null ${bailSig}`
            );
          }
        }
        if (stopSnap) {
          const vehSnap = snapPoint(
            distanceCtx.prepared,
            vehicle.latitude,
            vehicle.longitude
          );
          if (!vehSnap) {
            const bailKey = `${mission.id}:vehSnap_null`;
            const bailSig = `vehLL=${vehicle?.latitude?.toFixed(
              5
            )},${vehicle?.longitude?.toFixed(5)}`;
            if (rescueBailSigRef.current.get(bailKey) !== bailSig) {
              rescueBailSigRef.current.set(bailKey, bailSig);
              // eslint-disable-next-line no-console
              console.log(
                `[SIMHACK-rescue-bail] missionName=${mission.name} vehicleName=${vehicle?.name} reason=vehSnap_null ${bailSig}`
              );
            }
          }
          if (vehSnap) {
            const { meters: remaining, sameLine } = arcLengthBetween(
              distanceCtx.prepared,
              vehSnap,
              stopSnap
            );
            if (!sameLine) {
              const bailKey = `${mission.id}:sameLine_false`;
              const bailSig = `vehLine=${vehSnap.lineIdx}|stopLine=${
                stopSnap.lineIdx
              }|remaining=${remaining.toFixed(1)}`;
              if (rescueBailSigRef.current.get(bailKey) !== bailSig) {
                rescueBailSigRef.current.set(bailKey, bailSig);
                // eslint-disable-next-line no-console
                console.log(
                  `[SIMHACK-rescue-bail] missionName=${mission.name} vehicleName=${vehicle?.name} reason=sameLine_false ${bailSig}`
                );
              }
            }
            if (sameLine) {
              const rKey = `${mission.id}:${passedStops}:${mode}:remaining`;
              const prev = progressRatchetRef.current.get(rKey);
              const initialRem = prev?.initialRemaining ?? remaining;
              if (!(initialRem > 0)) {
                const bailKey = `${mission.id}:initialRem_zero`;
                const bailSig = `remaining=${remaining.toFixed(
                  1
                )}|initialRem=${initialRem.toFixed(1)}`;
                if (rescueBailSigRef.current.get(bailKey) !== bailSig) {
                  rescueBailSigRef.current.set(bailKey, bailSig);
                  // eslint-disable-next-line no-console
                  console.log(
                    `[SIMHACK-rescue-bail] missionName=${mission.name} vehicleName=${vehicle?.name} reason=initialRem_zero ${bailSig}`
                  );
                }
              }
              if (initialRem > 0) {
                let raw = clampPercent(
                  ((initialRem - remaining) / initialRem) * 100
                );
                // For cross-station missions (endIdx > 0), the rescue arc only
                // covers the first leg (vehicle→stop[0]). Rescale so the slider
                // lands at firstLeg/total when the vehicle reaches stop[0],
                // letting the default ratchet smoothly take over after the
                // walker bumps passedStops. Single-stop "Call Vehicle" missions
                // (endIdx === 0) skip this and behave exactly as before.
                if (endIdx > 0 && missionSnaps) {
                  const totalDist = sumLegs(
                    distanceCtx.prepared,
                    missionSnaps,
                    0,
                    endIdx
                  );
                  const firstLegDist = sumLegs(
                    distanceCtx.prepared,
                    missionSnaps,
                    0,
                    1
                  );
                  if (
                    totalDist !== null &&
                    totalDist > 0 &&
                    firstLegDist !== null
                  ) {
                    raw = clampPercent(raw * (firstLegDist / totalDist));
                  }
                }
                const next = Math.max(prev?.progress ?? 0, raw);
                progressRatchetRef.current.set(rKey, {
                  progress: next,
                  t: Date.now(),
                  initialRemaining: initialRem,
                });
                progress = next;
                skipDefaultRatchet = true;
                const okKey = `${mission.id}:rescue_ok`;
                const okSig = `initialRem=${initialRem.toFixed(
                  1
                )}|remaining=${remaining.toFixed(1)}|raw=${raw.toFixed(
                  1
                )}|next=${next.toFixed(1)}`;
                if (rescueBailSigRef.current.get(okKey) !== okSig) {
                  rescueBailSigRef.current.set(okKey, okSig);
                  // eslint-disable-next-line no-console
                  console.log(
                    `[SIMHACK-rescue-ok] missionName=${mission.name} vehicleName=${vehicle?.name} ${okSig}`
                  );
                }
              }
            }
          }
        }
      }

      // Mid-mission single-hop legs (both "approaching" the picked station and
      // "continuing"/departing FROM it): when snap-based distance is unavailable
      // (a stop won't snap, or the leg straddles two track LineStrings →
      // sameLine === false), computeDistanceProgress returns null and the
      // stop-count fallback collapses to a CONSTANT 100% — for approaching
      // because endIdx === passedStops (passedStops/endIdx === 1), and for
      // continuing because span === 1 and passedStops - startIdx === 1. That
      // pins the marker at the leg's far end for the whole leg: the map shows
      // smooth motion but the In Progress line never moves (and an approaching
      // leg "starts at" its destination). The passedStops === 0 rescues above
      // only cover the first leg out of dispatch; cover the remaining mid-
      // mission legs here with a straight-line haversine vehicle→endStop,
      // ratcheted (max-seen initial) so it animates monotonically even across
      // track-line boundaries. Pure great-circle is segment-ambiguity-free,
      // unlike snap arc-length. The approaching displayProgress inversion is
      // applied later, so measuring remaining-to-endStop is correct for both.
      if (
        !skipDefaultRatchet &&
        progressSrc === "fallback" &&
        (mode === "approaching" || mode === "continuing") &&
        passedStops > 0 &&
        vehicle &&
        endStop &&
        Number.isFinite(vehicle.latitude) &&
        Number.isFinite(vehicle.longitude) &&
        Number.isFinite(endStop.latitude) &&
        Number.isFinite(endStop.longitude)
      ) {
        const R = 6371008.8;
        const toRad = (deg: number) => (deg * Math.PI) / 180;
        const lat1 = toRad(vehicle.latitude);
        const lat2 = toRad(endStop.latitude);
        const dLat = lat2 - lat1;
        const dLng = toRad(endStop.longitude - vehicle.longitude);
        const sinDLat = Math.sin(dLat / 2);
        const sinDLng = Math.sin(dLng / 2);
        const h =
          sinDLat * sinDLat +
          Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
        const remaining = 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));

        // Loop-aware key (see the distance-path key below for the full
        // rationale): on a looping mission passedStops cycles back to the same
        // value every loop, so a key without passedLoops reuses the previous
        // loop's ratchet entry and pins this leg high from its start.
        const rKey = `${mission.id}:${
          mission.passedLoops ?? 0
        }:${passedStops}:${mode}:remaining`;
        const prev = progressRatchetRef.current.get(rKey);
        // Max-seen initial so a first sample taken while the vehicle lingers
        // near the prior stop (remaining already small) doesn't pin progress
        // high before it actually starts the leg.
        const initialRem = Math.max(prev?.initialRemaining ?? 0, remaining);
        if (initialRem > 0) {
          const raw = clampPercent(
            ((initialRem - remaining) / initialRem) * 100
          );
          const next = Math.max(prev?.progress ?? 0, raw);
          progressRatchetRef.current.set(rKey, {
            progress: next,
            t: Date.now(),
            initialRemaining: initialRem,
          });
          progress = next;
          skipDefaultRatchet = true;
        }
      }

      // The distance path (computeDistanceProgress) now returns a chord
      // projection that is already monotonic 0→100 for stop-to-stop travel and
      // free of wrong-lobe outliers, so it drives the bar directly with no
      // ratchet/velocity-cap/seed-at-0 smoothing. That smoothing previously
      // existed only to suppress arc-length snap outliers; with those gone it
      // would just add marker latency (and its arc-length denominator was
      // inconsistent with the chord, re-compressing the sweep). The haversine
      // rescue paths above keep their own ratchets for the fallback legs they
      // own. `progress` already holds the chord value from the assignment near
      // the top of this iteration.
      // Single-line signature so the console output isn't truncated by
      // Chrome's `…` and so we only re-log on real changes. Lat/lng rounded
      // to 5 decimals so jitter from telemetry doesn't spam.
      const vehLatR = vehicle?.latitude?.toFixed(5);
      const vehLngR = vehicle?.longitude?.toFixed(5);
      const sig =
        `veh=${vehicle?.name}|mode=${mode}|passed=${passedStops}` +
        `|seg=[${startIdx}]${startStop?.name ?? "?"}→[${endIdx}]${
          endStop?.name ?? "?"
        }` +
        `|vehLL=${vehLatR},${vehLngR}` +
        `|startLL=${startStop?.latitude?.toFixed(
          5
        )},${startStop?.longitude?.toFixed(5)}` +
        `|endLL=${endStop?.latitude?.toFixed(5)},${endStop?.longitude?.toFixed(
          5
        )}` +
        `|prog=${progress.toFixed(1)}|src=${progressSrc}`;
      if (simhackLastSigRef.current.get(mission.id) !== sig) {
        simhackLastSigRef.current.set(mission.id, sig);
        // eslint-disable-next-line no-console
        console.log(`[SIMHACK-prog] ${sig}`);
      }

      // In approaching mode, the selected station is the destination — put it
      // on the LEFT, with the most recent prior stop on the RIGHT, and invert
      // progress so the marker travels rightward as the vehicle gets farther
      // from origin (== closer to selected station). In continuing mode, keep
      // the natural anchor → end orientation. In departing mode, the selected
      // station is the origin — put it on the LEFT, the first stop on the
      // RIGHT, and use natural progress (marker moves left → right).
      let leftLabelStop = startStop;
      let rightLabelStop: QueuedMissionStop | undefined = endStop;
      let displayProgress = progress;
      let leftStationLabel: string | null = null;
      if (isDeparting) {
        // Use FE-tracked dispatch origin to resolve the label; stationStopId is
        // no longer relied on for this comparison (it derives from the mission's
        // first stop, not the operator's pick at dispatch time).
        const dispatchStopId =
          dispatchOrigins[mission.id] ?? mission.stationStopId;
        const dispatchStop = stops?.find((s) => s.id === dispatchStopId);
        leftStationLabel =
          dispatchStop?.shortName || dispatchStop?.name || "S1";
        rightLabelStop = endStop; // first stop = destination
      } else if (mode === "approaching") {
        leftLabelStop = endStop; // selected station (destination)
        if (endIdx > 0) {
          const priorStopIdx =
            passedStops > 0
              ? Math.min(passedStops - 1, endIdx - 1)
              : Math.max(0, endIdx - 1);
          rightLabelStop = mission.stops[priorStopIdx] ?? startStop;
        } else {
          // Single-stop mission (call vehicle): no real origin stop — leave
          // the right label blank so we don't duplicate the destination name.
          rightLabelStop = undefined;
        }
        displayProgress = clampPercent(100 - progress);
      }

      rows.push({
        id: `${mission.id}-${mode}-${endIdx}${isDeparting ? "-dep" : ""}`,
        name: vehicle?.name || mission.vehicle,
        imageUrl: vehicleType?.image?.link,
        batteryPercent: resolveLiveBattery(
          mission.vehicle,
          vehicle?.batteryLevel,
          vehiclesUpdateData
        ),
        missionText: mission.name || "Delivery in progress",
        isOffline: isVehicleOffline(mission.vehicle),
        startStation:
          leftStationLabel ?? resolveStationLabel(leftLabelStop, stops, "S1"),
        endStation:
          rightLabelStop !== undefined
            ? resolveStationLabel(rightLabelStop, stops, "S2")
            : "",
        progress: displayProgress,
        currentCycle: mission.passedLoops ?? 0,
        totalCycles: mission.totalLoops ?? 0,
        // On-screen travel direction of the indicator: approaching (TO) legs
        // invert progress so the marker runs right→left; every other leg
        // (departing/continuing FROM) runs left→right. Drives the row's exit
        // slide so it leaves the way the vehicle was heading.
        travelDirection:
          mode === "approaching" && !isDeparting ? "left" : "right",
      });
    }

    return rows;
  }, [
    fleetMissions,
    vehiclesDict,
    vehiclesUpdateData,
    vehicleTypes,
    stops,
    selectedStationId,
    preparedTrack,
    releasedStops,
    dispatchOrigins,
  ]);
};
